import { z } from 'zod';
import { Email, IsoDateTime, PageQuery, Uuid, page, withDefaults, patchOf } from './common.js';
/**
 * Module `evacuation` (docs/modules/evacuation.md, ADR 0021). Owner: task S21; S22 owns the FirePanel* and report shapes.
 * Builders may add optional fields; renaming or removing a field needs the architect.
 */
export const EvacuationKind = z.enum(['real', 'drill']);
export const EvacuationStatus = z.enum(['active', 'ended']);
export const RollCallStatus = z.enum(['unaccounted', 'safe', 'missing', 'off_site']);
/** 'building' (ADR 0034, S-SR2): started for a building evacuation of a shared reception. */
export const EvacuationSource = z.enum(['portal', 'kiosk', 'companion', 'fire_panel', 'system', 'building']);
/** Tie-break order for marks with the same time (ADR 0021 rule 1): first wins. */
export const MarkSource = z.enum(['marshal', 'portal', 'kiosk', 'self_app', 'self_contactless', 'fire_panel']);
// ---------- Muster points ----------
export const EvacuationPoint = z.object({
    id: Uuid,
    siteId: Uuid,
    name: z.string(),
    directions: z.string(),
    lat: z.number().nullable(),
    lng: z.number().nullable(),
    order: z.number().int(),
    active: z.boolean(),
});
export const EvacuationPointCreate = z.object({
    siteId: Uuid,
    name: z.string().trim().min(1).max(80),
    directions: z.string().trim().max(500).default(''),
    lat: z.number().min(-90).max(90).nullable().default(null),
    lng: z.number().min(-180).max(180).nullable().default(null),
});
export const EvacuationPointUpdate = patchOf(EvacuationPointCreate.omit({ siteId: true })).extend({ active: z.boolean().optional() });
/** PUT /evacuation/points/order */
export const EvacuationPointOrder = z.object({ siteId: Uuid, pointIds: z.array(Uuid).max(100) });
/** PUT /evacuation/members/:memberId/point (per-member override; null clears it). */
export const MemberPointOverride = z.object({ siteId: Uuid, pointId: Uuid.nullable() });
/** GET /evacuation/members/:memberId/point: the member's overrides, one per site that has one (sites on the default are left out). */
export const MemberPointOverrideList = z.array(z.object({ siteId: Uuid, pointId: Uuid }));
// ---------- Evacuations and the roll call ----------
export const Evacuation = z.object({
    id: Uuid,
    siteId: Uuid,
    kind: EvacuationKind,
    status: EvacuationStatus,
    startedAt: IsoDateTime,
    startedSource: EvacuationSource,
    startedByLabel: z.string(),
    endedAt: IsoDateTime.nullable(),
    endedByLabel: z.string().nullable(),
    counts: z.object({ total: z.number().int(), unaccounted: z.number().int(), safe: z.number().int(), missing: z.number().int(), offSite: z.number().int() }),
    /** Built after the end (and rebuilt after late marks). */
    reportReady: z.boolean(),
    updatedAfterEnd: z.boolean(),
});
export const RollCallPerson = z.object({
    id: Uuid,
    evacuationId: Uuid,
    kind: z.enum(['visit', 'assumed_member']),
    visitId: Uuid.nullable(),
    memberId: Uuid.nullable(),
    name: z.string(),
    groupName: z.string(),
    isVisitor: z.boolean(),
    hostName: z.string().nullable(),
    pointId: Uuid.nullable(),
    status: RollCallStatus,
    statusAt: IsoDateTime.nullable(),
    statusSource: MarkSource.nullable(),
    statusByLabel: z.string().nullable(),
    openPermits: z.number().int(),
    /** Marks from different actors disagreed within the conflict window (ADR 0021 rule 3). */
    check: z.boolean(),
    /** e.g. "signed out at 10:04", "signed in during the evacuation". i18n keys with params. */
    notes: z.array(z.object({ key: z.string(), params: z.record(z.string(), z.string()) })),
    /** Self "I'm safe" location, only when allowed; rounded. */
    selfLocation: z.object({ lat: z.number(), lng: z.number(), accuracyM: z.number() }).nullable(),
    /** When `check` is true: the disagreeing marks (ADR 0021 rule 3), oldest first, for "Safe at Car park, by the app, 10:03; Missing, by J. Smith, 10:04". */
    checkMarks: z.array(z.object({ status: RollCallStatus, pointId: Uuid.nullable(), source: MarkSource, byLabel: z.string(), at: IsoDateTime })).optional(),
});
export const EvacuationView = z.object({
    evacuation: Evacuation,
    points: z.array(EvacuationPoint),
    people: z.array(RollCallPerson),
    /** Only on GET /evacuation/me/active: the tenant lets "I'm safe" carry the phone position. Absent = no. */
    selfMarkLocation: z.boolean().optional(),
});
export const EvacuationStart = z.object({
    siteId: Uuid,
    kind: EvacuationKind.default('real'),
    /** Device-made id (offline start) or omitted for a new one. */
    id: Uuid.optional(),
    occurredAt: IsoDateTime.optional(),
});
/** POST /evacuation/device/:id/end: the site PIN (needed when the site's kiosk start mode is `pin`). */
export const EvacuationDeviceEnd = z.object({ pin: z.string().max(20).nullable().default(null) });
/** POST /evacuation/device/start: the start plus the site PIN (when the site's kiosk start mode is `pin`). */
export const EvacuationDeviceStart = EvacuationStart.extend({ pin: z.string().max(20).nullable().default(null) });
/** GET /evacuation/me/active: which site, when the member can see several. */
export const EvacuationMeActiveQuery = z.object({ siteId: Uuid.optional() });
export const EvacuationStartResult = z.object({ evacuation: Evacuation, alreadyActive: z.boolean() });
/** One status change for one person, from any device. Idempotent by key. */
export const EvacuationMark = z.object({
    idempotencyKey: Uuid,
    personId: Uuid,
    status: RollCallStatus,
    pointId: Uuid.nullable().default(null),
    occurredAt: IsoDateTime,
});
export const EvacuationMarkResult = z.object({ applied: z.boolean(), duplicate: z.boolean(), person: RollCallPerson });
/** POST /evacuation/device/sync and /evacuation/me/sync (ADR 0021 "Starting offline and merging"). */
export const EvacuationSync = z.object({
    sentAt: IsoDateTime,
    evacuation: z.object({ id: Uuid, siteId: Uuid, kind: EvacuationKind, startedAt: IsoDateTime }).nullable(),
    /** People the device added locally (by visit id it knows), so marks can name them before the server row exists. */
    localPeople: z.array(z.object({ localId: Uuid, visitId: Uuid.nullable(), memberId: Uuid.nullable() })).max(2000).default([]),
    marks: z.array(EvacuationMark.extend({ personId: Uuid })).max(2000).default([]),
});
export const EvacuationSyncResult = z.object({
    evacuationId: Uuid.nullable(),
    /** localId -> server person id. */
    people: z.record(z.string(), Uuid),
    results: z.array(z.object({ idempotencyKey: Uuid, applied: z.boolean(), duplicate: z.boolean() })),
});
/** "I'm safe" from the Companion app or the contactless page. */
export const ImSafe = z.object({
    idempotencyKey: Uuid,
    occurredAt: IsoDateTime,
    pointId: Uuid.nullable().default(null),
    location: z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180), accuracyM: z.number().min(0).max(10000) }).nullable().default(null),
});
export const EvacuationListQuery = PageQuery.extend({ siteId: Uuid.optional(), kind: EvacuationKind.optional(), from: z.string().optional(), to: z.string().optional() });
export const EvacuationList = page(Evacuation);
/** Live channel `evacuation` (ADR 0021). */
export const EvacuationLiveMessage = z.discriminatedUnion('type', [
    z.object({ type: z.literal('evac.state'), evacuation: Evacuation }),
    z.object({ type: z.literal('evac.person'), person: RollCallPerson }),
    z.object({ type: z.literal('refresh') }),
]);
/** Live channel `site-alert`: no names, for displays, kiosks and the contactless page. */
export const SiteAlertState = z.object({
    evacuationId: Uuid.nullable(),
    kind: EvacuationKind.nullable(),
    status: z.enum(['none', 'active']),
    startedAt: IsoDateTime.nullable(),
    points: z.array(z.object({ id: Uuid, name: z.string(), directions: z.string() })),
    /** Only on GET /evacuation/me/active: the tenant lets "I'm safe" carry the phone position. Absent = no. */
    selfMarkLocation: z.boolean().optional(),
});
/** Kiosk config section `evacuation`. */
export const KioskEvacuationSection = z.object({
    points: z.array(EvacuationPoint),
    /** 'off' | 'pin' (site evacuation PIN, h2$ form as group PINs) | 'open'. */
    kioskStart: z.enum(['off', 'pin', 'open']),
    pinHash: z.string().nullable(),
    /** Whether this site's kiosks may start a drill (site setting kioskDrill). */
    kioskDrill: z.boolean().optional(),
    active: Evacuation.nullable(),
});
// ---------- Fire panels (task S22) ----------
export const FirePanelMode = z.enum(['signed', 'token']);
export const FirePanel = z.object({
    id: Uuid,
    siteId: Uuid,
    name: z.string(),
    mode: FirePanelMode,
    allowedCidrs: z.array(z.string()),
    canEndAny: z.boolean(),
    enabled: z.boolean(),
    url: z.string(),
    lastCallAt: IsoDateTime.nullable(),
    lastCallResult: z.string().nullable(),
    createdAt: IsoDateTime,
});
export const FirePanelCreate = z
    .object({
    siteId: Uuid,
    name: z.string().trim().min(1).max(80),
    mode: FirePanelMode.default('signed'),
    allowedCidrs: z.array(z.string().max(50)).max(20).default([]),
    canEndAny: z.boolean().default(false),
})
    .refine((p) => p.mode === 'signed' || p.allowedCidrs.length > 0, { message: 'token mode needs an address allow-list', path: ['allowedCidrs'] });
export const FirePanelUpdate = z.object({
    name: z.string().trim().min(1).max(80).optional(),
    allowedCidrs: z.array(z.string().max(50)).max(20).optional(),
    canEndAny: z.boolean().optional(),
    enabled: z.boolean().optional(),
});
/** Create and rotate answer with the secret, shown once. */
export const FirePanelSecret = z.object({ panel: FirePanel, secret: z.string() });
/** Body of POST /evacuation/hooks/fire-panel/:panelId. */
export const FirePanelCall = z.object({ action: z.enum(['start', 'end', 'test']), kind: EvacuationKind.default('real'), zone: z.string().max(80).optional() });
export const FirePanelCallResult = z.object({ ok: z.literal(true), duplicate: z.boolean(), evacuationId: Uuid.nullable() });
// ---------- Report (task S22) ----------
export const EvacuationReport = z.object({
    evacuation: Evacuation,
    durationSeconds: z.number().int().nullable(),
    /** Seconds from start until the last person was accounted for (safe or off site), or null. */
    allAccountedSeconds: z.number().int().nullable(),
    missing: z.array(z.object({ name: z.string(), groupName: z.string(), hostName: z.string().nullable() })),
    timeline: z.array(z.object({ at: IsoDateTime, name: z.string(), status: RollCallStatus, source: MarkSource, byLabel: z.string(), applied: z.boolean() })),
    byPoint: z.array(z.object({ pointId: Uuid.nullable(), pointName: z.string(), safe: z.number().int() })),
});
// ---------- Settings ----------
const AlertTargets = z.object({
    onSite: z.boolean(),
    marshals: z.boolean(),
    hosts: z.boolean(),
    channels: z.array(z.enum(['push', 'sms', 'email'])),
});
export const EvacuationTenantSettings = z.object({
    /** Days to keep evacuations, roll calls and reports; 0 = keep (ADR 0025). */
    retentionDays: z.number().int().min(0).default(1095),
    conflictWindowMinutes: z.number().int().min(0).max(60).default(5),
    /** "I'm safe" may record the phone's position (only during an evacuation). */
    selfMarkLocation: z.boolean().default(false),
    alertsReal: withDefaults(z.object({
        onSite: z.boolean().default(true),
        marshals: z.boolean().default(true),
        hosts: z.boolean().default(true),
        channels: z.array(z.enum(['push', 'sms', 'email'])).default(['push', 'sms', 'email']),
    })),
    alertsDrill: withDefaults(z.object({
        onSite: z.boolean().default(false),
        marshals: z.boolean().default(true),
        hosts: z.boolean().default(false),
        channels: z.array(z.enum(['push', 'email'])).default(['push']),
    })),
});
export const EvacuationSiteSettings = z.object({
    defaultPointId: Uuid.nullable().default(null),
    /** Who may start an evacuation on this site's kiosks (ADR 0021). The PIN is set with PUT /evacuation/sites/:id/kiosk-pin. */
    kioskStart: z.enum(['off', 'pin', 'open']).default('pin'),
    /** Whether a kiosk may start a drill (Phase 4, S54). Off by default: drills come from the portal and marshal phones. */
    kioskDrill: z.boolean().default(false),
    /** Members who act as marshals here (get alerts; see the list in the app with the group's evacuationList permission). */
    marshalMemberIds: z.array(Uuid).max(200).default([]),
    /** Emailed when an evacuation starts and when its report is ready. */
    contactEmails: z.array(Email).max(20).default([]),
});
export const EvacuationGroupSettings = z.object({
    showOnList: z.boolean().default(true),
    /** Visitors take their host's muster point. */
    evacuateWithHost: z.boolean().default(false),
    /** siteId -> pointId */
    defaultPoints: z.record(z.string(), Uuid).default({}),
});
export const EvacuationKioskPinSet = z.object({ pin: z.string().regex(/^\d{4,8}$/).nullable() });
//# sourceMappingURL=evacuation.js.map